import { createFileRoute } from '@tanstack/react-router';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import {
  ActionIcon, Button, Center, Group, Loader, Pagination, ScrollArea, SimpleGrid, Table, Text, TextInput, Tooltip, rem,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconActivity, IconAlertTriangle, IconArrowsRightLeft, IconCalendar, IconClock, IconDownload,
  IconRefresh, IconSearch, IconUserShield, IconX,
} from '@tabler/icons-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { fetchAllPages } from '@/shared/api/jsonapi/client';
import { StatCard } from '@/shared/components/StatCard';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileCrossTenantLogs } from '@/features/admin/mobile/MobileCrossTenantLogs';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

/**
 * Attributi reali dell'entità BE `CrossTenantAccessLog` (aggregato giornaliero).
 * NB: non esistono relationship JSON:API (ActorUserId/TargetTenantId sono FK semplici);
 * i tenant si risolvono per id via la lista `tenants`. `targetTenantId` è esposto come
 * [Attr] dal Bez v1.8.x in poi — se assente si degrada a "—".
 */
interface CrossTenantAccessLog extends Denormalized {
  type: 'cross-tenant-access-logs';
  actorUsername?: string;
  actorTenantId?: string;
  targetTenantId?: string;
  accessDate?: string;
  firstAccessAt?: string;
  lastAccessAt?: string;
  accessCount?: number;
  ip?: string;
  userAgent?: string;
}

interface TenantRow extends Denormalized { type: 'tenants'; name?: string }

export const Route = createFileRoute('/_app/admin/cross-tenant-access-logs')({
  component: CrossTenantAccessLogsRoute,
});

/** Selettore: audit cross-tenant mobile (MaeDesign) vs tabella desktop. */
function CrossTenantAccessLogsRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileCrossTenantLogs /> : <CrossTenantAccessLogsPage />;
}

const PAGE_SIZE = 25;

function CrossTenantAccessLogsPage() {
  const qc = useQueryClient();
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const listQ = useQuery({
    queryKey: ['cross-tenant-access-logs'],
    queryFn: () => fetchAllPages<CrossTenantAccessLog>('cross-tenant-access-logs', {
      sort: ['-accessDate']
    }, 500),
  });

  // Risoluzione id → nome tenant (il SuperAdmin legge la lista completa dei tenant).
  const tenantsQ = useQuery({
    queryKey: ['tenants-name-map'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllPages<TenantRow>('tenants', { fields: { tenants: ['name'] } }, 200),
  });

  const tenantName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenantsQ.data ?? []) if (t.name) m.set(t.id, t.name);
    return (id?: string) => (id ? (m.get(id) ?? id) : undefined);
  }, [tenantsQ.data]);

  const all = useMemo(() => listQ.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const [from, to] = range;
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      const d = row.accessDate;
      if (from && d && new Date(d) < dayjs(from).startOf('day').toDate()) return false;
      if (to && d && new Date(d) > dayjs(to).endOf('day').toDate()) return false;
      if (term) {
        const hay = `${row.actorUsername ?? ''} ${tenantName(row.actorTenantId) ?? ''} ${tenantName(row.targetTenantId) ?? ''} ${row.ip ?? ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [all, range, search, tenantName]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const stats = useMemo(() => {
    const accesses = all.reduce((s, r) => s + (r.accessCount ?? 0), 0);
    const actors = new Set(all.map((r) => r.actorUsername).filter(Boolean)).size;
    const last = all.reduce<string | null>((mx, r) => {
      const v = r.lastAccessAt ?? r.accessDate;
      return v && (!mx || v > mx) ? v : mx;
    }, null);
    return { rows: all.length, accesses, actors, last };
  }, [all]);

  const CSV_COLUMNS: Array<CsvColumn<CrossTenantAccessLog>> = [
    { header: 'Giorno', accessor: (r) => r.accessDate },
    { header: 'Actor', accessor: (r) => r.actorUsername },
    { header: 'Azienda d’origine', accessor: (r) => tenantName(r.actorTenantId) },
    { header: 'Azienda di destinazione', accessor: (r) => tenantName(r.targetTenantId) },
    { header: 'N. richieste', accessor: (r) => r.accessCount ?? 0 },
    { header: 'IP', accessor: (r) => r.ip },
  ];

  const exportCsv = () => {
    if (filtered.length === 0) {
      notifications.show({ color: 'orange', message: 'Nessun dato da esportare' });
      return;
    }
    downloadCsv(`cross-tenant-access-logs-${dayjs().format('YYYYMMDD-HHmm')}`, CSV_COLUMNS, filtered);
    notifications.show({ color: 'green', message: `${filtered.length} righe esportate` });
  };

  const card: CSSProperties = {
    border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
  };
  const thStyle: CSSProperties = {
    background: mw.surface2, color: mw.ink300, fontSize: 'var(--fs-compact)', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${mw.line}`, whiteSpace: 'nowrap',
  };
  const chip = (label: string, bg: string, color: string, codice = false) => (
    <span style={{ display: 'inline-block', fontSize: codice ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(codice ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
  );
  const initials = (s?: string | null) => {
    const parts = (s ?? '').trim().split(/[\s@._-]+/).filter(Boolean);
    return (parts.map((w) => w[0]).slice(0, 2).join('') || '?').toUpperCase();
  };

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconArrowsRightLeft size={22} />}
        title="Accessi fra aziende"
        sub="Operazioni fatte da un utente su un’azienda diversa dalla sua, aggregate per giorno."
        tone="bordeaux"
        actions={
          <>
            <Tooltip label="Aggiorna">
              <ActionIcon variant="light" color="dark" size="lg" aria-label="Aggiorna"
                onClick={() => qc.invalidateQueries({ queryKey: ['cross-tenant-access-logs'] })}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button variant="light" color="dark" leftSection={<IconDownload size={16} />}
              onClick={exportCsv} disabled={filtered.length === 0}>
              Esporta CSV
            </Button>
          </>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={14} mb={18}>
        <StatCard label="Aggregati giornalieri" value={stats.rows} icon={IconArrowsRightLeft} color="brand" loading={listQ.isLoading} />
        <StatCard label="Accessi totali" value={stats.accesses} icon={IconActivity} color="blue" loading={listQ.isLoading} />
        <StatCard label="Attori distinti" value={stats.actors} icon={IconUserShield} color="grape" loading={listQ.isLoading} />
        <StatCard label="Ultimo accesso" value={stats.last ? dayjs(stats.last).format('DD/MM/YYYY') : '—'} icon={IconClock} color="gray" loading={listQ.isLoading} />
      </SimpleGrid>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${mw.line}`, background: mw.accentTint, borderRadius: 14, padding: '12px 16px', marginBottom: 16 }}>
        <IconArrowsRightLeft size={18} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
          Storico delle operazioni fra aziende: un utente dell’azienda <em>origine</em> opera su un’azienda <em>target</em> discendente passando l&apos;header <code>tenantId</code>. Sola lettura, aggregato per giorno × attore × target.
        </Text>
      </div>

      <div style={{ ...card, padding: '14px 16px', marginBottom: 16 }}>
        <Group wrap="wrap" gap="sm">
          <DatePickerInput
            type="range"
            placeholder="Periodo"
            value={range}
            onChange={(v) => setRange(v as [Date | null, Date | null])}
            leftSection={<IconCalendar size={14} />}
            clearable
            w={260}
            valueFormat="DD/MM/YYYY"
          />
          <TextInput
            placeholder="Cerca attore, azienda o IP…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={280}
          />
          <div style={{ marginLeft: 'auto' }}>
            {filtered.length !== all.length
              ? chip(`${filtered.length} di ${all.length}`, mw.accentTint, mw.accentText)
              : chip(`${all.length} aggregati`, mw.surface2, mw.ink500)}
          </div>
        </Group>
      </div>

      {listQ.isLoading ? (
        <Center py={60}><Loader /></Center>
      ) : listQ.isError ? (
        <div style={{ ...card, padding: '28px 20px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: mw.dangerBg, color: mw.danger, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconAlertTriangle size={22} />
          </span>
          <div>
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>Impossibile caricare i log degli accessi fra aziende</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>Verifica di avere i permessi SuperAdmin, poi riprova.</Text>
            <Button mt={12} size="xs" variant="light" color="dark" leftSection={<IconRefresh size={14} />}
              onClick={() => qc.invalidateQueries({ queryKey: ['cross-tenant-access-logs'] })}>
              Riprova
            </Button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '36px 20px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 14, background: mw.brandTint, color: mw.brand, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <IconArrowsRightLeft size={26} />
          </span>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>
            {all.length === 0 ? 'Nessun accesso fra aziende registrato' : 'Nessun risultato per i filtri correnti'}
          </Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>
            {all.length === 0
              ? "Le operazioni fra aziende compariranno qui (aggregate giornalmente) appena qualcuno userà l'header tenantId."
              : 'Prova ad allargare il periodo o pulire la ricerca.'}
          </Text>
        </div>
      ) : (
        <>
          <div style={{ ...card, overflow: 'hidden' }}>
            <ScrollArea type="auto">
              <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={thStyle}>Giorno</Table.Th>
                    <Table.Th style={thStyle}>Attore</Table.Th>
                    <Table.Th style={thStyle}>Azienda d’origine</Table.Th>
                    <Table.Th style={thStyle}>Azienda di destinazione</Table.Th>
                    <Table.Th style={{ ...thStyle, textAlign: 'right' }}>Accessi</Table.Th>
                    <Table.Th style={thStyle}>IP</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageData.map((row) => {
                    const actor = row.actorUsername ?? '—';
                    const origin = tenantName(row.actorTenantId);
                    const target = tenantName(row.targetTenantId);
                    const first = row.firstAccessAt ? dayjs(row.firstAccessAt).format('HH:mm') : null;
                    const last = row.lastAccessAt ? dayjs(row.lastAccessAt).format('HH:mm') : null;
                    const span = first && last ? (first === last ? first : `${first}–${last}`) : null;
                    return (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{row.accessDate ? dayjs(row.accessDate).format('DD/MM/YYYY') : '—'}</Text>
                          {span && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{span}</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={9} wrap="nowrap">
                            <span style={{ width: rem(30), height: rem(30), borderRadius: 9, flexShrink: 0, background: mw.brandFill, color: 'var(--on-brand)', fontSize: 'var(--fs-compact)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(actor)}</span>
                            <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }} lineClamp={1}>{actor}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>{origin ? chip(origin, mw.surface2, mw.ink500) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                        <Table.Td>{target ? chip(target, mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900, fontVariantNumeric: 'tabular-nums' }}>{row.accessCount ?? 0}</Text>
                        </Table.Td>
                        <Table.Td>{row.ip ? chip(row.ip, mw.surface2, mw.ink500, true) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </div>
          {totalPages > 1 && (
            <Group justify="center" mt={16}>
              {/* `color="brand"` e non `mw.brand`: Pagination è l'unico di questi controlli
                  che fa **matematica** sul colore (`darken()` per l'hover, `rgba()` per la
                  pagina non attiva), e su un `var(--…)` quel calcolo non riesce a leggere il
                  valore → l'hover diventava nero. La chiave del tema è già sensibile allo
                  schema, quindi il bordeaux resta bordeaux senza passare da una variabile. */}
              <Pagination total={totalPages} value={safePage} onChange={setPage} size="sm" color="brand" />
            </Group>
          )}
        </>
      )}
    </MaeShell>
  );
}
